import type { QuerySpec } from '@helios/powersync-client-core';
import { allergyReviewId } from './allergy-review-logic';

/* ------------------------------------------------------------------ */
/*  Extracted allergy review — queries + write statements (PRD.272).   */
/*                                                                      */
/*  Review rows persist per-extraction, per-mention state + provenance  */
/*  (visit_extraction_allergy_reviews). Allergy creation/restoration    */
/*  reuses the existing patient_allergies write path (additive INSERT / */
/*  restore via deleted_at = NULL) — the review flow never adds         */
/*  speculative columns to patient_allergies.                           */
/* ------------------------------------------------------------------ */

// Pure classification/state logic lives in a dependency-light module.
export {
  classifyExtractedAllergy,
  initialStatusFor,
  statusForAction,
  defaultSelected,
  isSelectable,
  requiresAttention,
  dedupeAllergyMentions,
  findActiveExactMatch,
  buildAllergyReviewProvenance,
  allergyReviewId,
  unresolvedAllergyCount,
} from './allergy-review-logic';
export type {
  AllergyClassification,
  AllergyReviewStatus,
  AllergyResolutionAction,
  AllergyRef,
  AllergyClassificationResult,
  AllergyReviewProvenance,
} from './allergy-review-logic';

// ─── Row Type ───

export interface VisitExtractionAllergyReviewRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  patient_id: string | null;
  visit_id: string | null;
  extraction_id: string | null;
  source_recording_id: string | null;
  raw_value: string | null;
  normalized_value: string | null;
  status: string | null;
  resolution_action: string | null;
  resolved_allergy_id: string | null;
  confirmed_by: string | null;
  confirmed_at: string | null;
  dismissed_by: string | null;
  dismissed_at: string | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
}

// ─── Queries ───

/** Active review items for an extraction (oldest first). */
export function qAllergyReviewsByExtraction(extractionId: string): QuerySpec<[string]> {
  return {
    key: ['visit_extraction_allergy_reviews', 'by-extraction', extractionId] as const,
    sql: `SELECT * FROM visit_extraction_allergy_reviews
          WHERE extraction_id = ? AND deleted_at IS NULL
          ORDER BY created_at ASC`,
    params: [extractionId],
    watch: ['visit_extraction_allergy_reviews'],
  };
}

/** Soft-deleted (previously removed) allergies for a patient — for classification. */
export function qDeletedPatientAllergies(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient_allergies', 'deleted-by-patient', patientId] as const,
    sql: `SELECT * FROM patient_allergies
          WHERE patient_id = ? AND deleted_at IS NOT NULL
          ORDER BY updated_at DESC`,
    params: [patientId],
    watch: ['patient_allergies'],
  };
}

// ─── Write statements (executed via PowerSync db.execute) ───

export interface UpsertAllergyReviewInput {
  orgId: string;
  clinicId: string | null;
  patientId: string;
  visitId: string;
  extractionId: string;
  sourceRecordingId: string | null;
  rawValue: string;
  normalizedValue: string;
  status: string;
  resolutionAction: string | null;
  resolvedAllergyId: string | null;
  userId: string | null;
  now: string;
  /** True when the resolution is a dismissal (records dismissed_by/at). */
  dismissed?: boolean;
}

/**
 * Idempotently persist a review row's resolution. Uses a deterministic id per
 * (extraction, normalized value) via INSERT OR REPLACE, so re-resolving the
 * same mention updates one row rather than piling up duplicates. Review rows
 * are written ONLY on an explicit resolution — pending state is derived (no
 * pre-seeding), which avoids overwriting a resolved row from another device.
 */
export function upsertAllergyReviewStmt(input: UpsertAllergyReviewInput): {
  sql: string;
  params: unknown[];
} {
  const id = allergyReviewId(input.extractionId, input.normalizedValue);
  const confirmedBy = input.dismissed ? null : input.userId;
  const confirmedAt = input.dismissed ? null : input.now;
  const dismissedBy = input.dismissed ? input.userId : null;
  const dismissedAt = input.dismissed ? input.now : null;
  return {
    sql: `INSERT OR REPLACE INTO visit_extraction_allergy_reviews
            (id, org_id, clinic_id, patient_id, visit_id, extraction_id,
             source_recording_id, raw_value, normalized_value, status,
             resolution_action, resolved_allergy_id,
             confirmed_by, confirmed_at, dismissed_by, dismissed_at,
             created_by, created_at, updated_at)
          VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    params: [
      id,
      input.orgId,
      input.clinicId,
      input.patientId,
      input.visitId,
      input.extractionId,
      input.sourceRecordingId,
      input.rawValue,
      input.normalizedValue,
      input.status,
      input.resolutionAction,
      input.resolvedAllergyId,
      confirmedBy,
      confirmedAt,
      dismissedBy,
      dismissedAt,
      input.userId,
      input.now,
      input.now,
    ],
  };
}

// ─── Existing patient_allergies write path (reused, additive) ───

export interface CreatePatientAllergyInput {
  id: string;
  patientId: string;
  /** Clinician-chosen (never invented): 'drug' | 'food' | null. */
  allergyType: string | null;
  substance: string;
  reaction: string | null;
  severity: string | null;
  userId: string | null;
  now: string;
}

/**
 * Additively INSERT a single patient_allergies row — reuses the exact columns
 * of the existing allergy write path (id, patient_id, allergy_type, substance,
 * reaction, severity) and never soft-deletes or rewrites other allergies.
 */
export function createPatientAllergyStmt(input: CreatePatientAllergyInput): {
  sql: string;
  params: unknown[];
} {
  // NOTE: only columns present in the PowerSync client schema for
  // patient_allergies may be written (id, patient_id, allergy_type, substance,
  // reaction, severity, deleted_at). `updated_at`/`updated_by` are NOT in the
  // client view — inserting them raises "no column named updated_at" and the
  // review write silently fails. The server (syncwrite) stamps updated_at.
  return {
    sql: `INSERT INTO patient_allergies
            (id, patient_id, allergy_type, substance, reaction, severity)
          VALUES (?, ?, ?, ?, ?, ?)`,
    params: [
      input.id,
      input.patientId,
      input.allergyType,
      input.substance,
      input.reaction,
      input.severity,
    ],
  };
}

/** Restore a soft-deleted allergy in place (deleted_at → NULL). */
export function restorePatientAllergyStmt(input: {
  id: string;
  userId: string | null;
  now: string;
}): { sql: string; params: [string] } {
  // Only `deleted_at` is in the patient_allergies client schema alongside the
  // core columns — `updated_at`/`updated_by` are not (see createPatientAllergyStmt).
  return {
    sql: `UPDATE patient_allergies
          SET deleted_at = NULL
          WHERE id = ?`,
    params: [input.id],
  };
}
